
import React, { useState, useEffect } from "react";
import { useStudents } from "../hooks/useStudents";
import { saveCertificate, getCertificate, updateCertificate } from "../firebase/database";
import { generateCertificatePDF } from "../utils/certificateGenerator";
import { useNavigate, useParams } from "react-router-dom";

export default function IssueCertificate() {
  const { certId } = useParams();
  const isEdit = !!certId;
  const { students, loading } = useStudents();
  const [selectedStudent, setSelectedStudent] = useState("");
  const [courseName, setCourseName] = useState("");
  const [skills, setSkills] = useState("");
  const [residence, setResidence] = useState("");
  const [certNo, setCertNo] = useState("");
  const [date, setDate] = useState(new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
  const [photo, setPhoto] = useState(null);
  const [signatories, setSignatories] = useState([]);
  const [isIssuing, setIsIssuing] = useState(false);
  
  const navigate = useNavigate();

  const staff = students.filter(s => s.role === "staff" || s.id.startsWith("STAFF-"));

  useEffect(() => {
    if (isEdit) {
      getCertificate(certId).then(data => {
        if (data) {
          setSelectedStudent(data.studentId);
          setCourseName(data.courseName);
          setSkills(data.skills);
          setResidence(data.residence);
          setCertNo(data.certNo);
          setDate(data.date);
          if (data.photo) setPhoto(data.photo);
          if (data.signatories) setSignatories(data.signatories);
        }
      });
    }
  }, [isEdit, certId]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleSignatory = (staffMember) => {
    setSignatories(prev => {
      const exists = prev.find(s => s.id === staffMember.id);
      if (exists) {
        return prev.filter(s => s.id !== staffMember.id);
      }
      if (prev.length >= 3) {
        alert("You can select up to 3 signatories");
        return prev;
      }
      return [...prev, {
        id: staffMember.id,
        name: staffMember.name,
        designation: staffMember.designation || "Authority",
        signatureUrl: staffMember.signatureUrl || null
      }];
    });
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !courseName) return alert("Please fill all details");
    if (signatories.length < 1) return alert("Please select at least one signatory");

    setIsIssuing(true);
    try {
      const student = students.find(s => s.id === selectedStudent);
      const certData = {
        studentId: student.id,
        studentName: student.name,
        courseName,
        skills,
        residence,
        certNo,
        date,
        photo,
        signatories,
      };

      let finalCertId = certId;
      if (isEdit) {
        await updateCertificate(certId, certData);
      } else {
        finalCertId = await saveCertificate(certData);
      }

      await generateCertificatePDF(certData, finalCertId);
      alert(isEdit ? "Certificate updated successfully!" : "Certificate issued successfully!");
      navigate("/admin/certificates");
    } catch (error) {
      console.error("Error saving certificate:", error);
      alert("Failed to save certificate");
    } finally {
      setIsIssuing(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading details...</div>;

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: 20, fontFamily: "DM Sans, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h2 style={{ color: "#111827" }}>{isEdit ? "Edit Certificate" : "Issue Certificate"}</h2>
        <button onClick={() => navigate("/admin/certificates")} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer", fontSize: 14 }}>
          View All Certificates
        </button>
      </div>
      <form onSubmit={handleIssue} style={{ display: "grid", gap: 20, background: "#fff", padding: 24, borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Select Student</label>
            <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required disabled={isEdit}>
              <option value="">Choose a student</option>
              {students.filter(s => s.role !== "staff" && !s.id.startsWith("STAFF-")).map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Certificate No.</label>
            <input type="text" value={certNo} onChange={(e) => setCertNo(e.target.value)} placeholder="e.g. CE/2024-25/46" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required />
          </div>
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Residence (R/o)</label>
          <input type="text" value={residence} onChange={(e) => setResidence(e.target.value)} placeholder="Punjab" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Course Name</label>
          <input type="text" value={courseName} onChange={(e) => setCourseName(e.target.value)} placeholder="COURSE OF FRONTEND WEB DESIGNING" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Skills</label>
          <input type="text" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="HTML, CSS, JS, WORDPRESS" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required />
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Select Signing Authorities (Max 3)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: 12, border: "1px solid #D1D5DB", borderRadius: 8, background: "#F9FAFB" }}>
            {staff.map(member => {
              const isSelected = signatories.find(s => s.id === member.id);
              return (
                <div key={member.id} onClick={() => toggleSignatory(member)} style={{ 
                  padding: "6px 12px", borderRadius: 20, border: `1px solid ${isSelected ? "#2563EB" : "#D1D5DB"}`,
                  background: isSelected ? "#EFF6FF" : "#fff", color: isSelected ? "#2563EB" : "#374151",
                  fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  transition: "all 0.2s"
                }}>
                  {member.name} ({member.designation || "Staff"})
                  {isSelected && <span>✓</span>}
                </div>
              );
            })}
            {staff.length === 0 && <span style={{ fontSize: 12, color: "#6B7280" }}>No staff members found</span>}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "#6B7280" }}>
            Selected: {signatories.map(s => s.name).join(", ") || "None"}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Upload Photo (Passport Size)</label>
            <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ width: "100%", padding: "8px 0" }} />
            {photo && (
              <div style={{ marginTop: 10 }}>
                <img src={photo} alt="Preview" style={{ width: 80, height: 100, objectFit: "cover", borderRadius: 4, border: "1px solid #D1D5DB" }} />
              </div>
            )}
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Date</label>
            <input type="text" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #D1D5DB" }} required />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="submit" disabled={isIssuing} style={{ width: "100%", maxWidth: 300, padding: "12px", borderRadius: 8, border: "none", backgroundColor: "#2563EB", color: "#fff", fontWeight: 600, cursor: isIssuing ? "not-allowed" : "pointer", opacity: isIssuing ? 0.7 : 1 }}>
            {isIssuing ? "Saving..." : (isEdit ? "Update & Download PDF" : "Issue & Download PDF")}
          </button>
        </div>
      </form>
    </div>
  );
}
